import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FormPasswordInput from '../../components/forms/FormPasswordInput';
import FormInput from '../../components/forms/FormInput';
import { CheckCircle } from 'lucide-react';
import Button from '../../components/layout/ui/Button';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);

      const type = userData?.profile_type || userData?.type;

      if (type === 'nanny') {
        navigate('/dashboard/nanny');
      } else if (type === 'agent') {
        navigate('/dashboard/agent');
      } else if (type === 'customer') {
        navigate('/dashboard/customer');
      } else {
        navigate('/dashboard/admin');
      }
    } catch (err) {
      alert('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/nanny.jpg" alt="Domestic worker platform" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/70 to-secondary/80" />
      </div>

      <div className="relative z-10 max-w-4xl w-full grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
        <div className="bg-gradient-to-br from-primary to-secondary p-12 text-white flex flex-col justify-center">
          <img src="/mainlogowhite.png" alt="AuthWorka Logo" className="w-64 md:w-72 h-auto mb-6" />
          <p className="text-2xl opacity-90 mb-8 font-light">
            Trusted. Structured. Professional.
          </p>

          <div className="hidden md:block space-y-5 text-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                <CheckCircle />
              </div>
              <span>Trusted domestic workers with stronger verification workflows</span>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                <CheckCircle />
              </div>
              <span>Structured offers, contracts, and support tools</span>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                <CheckCircle />
              </div>
              <span>Built for employers, workers, agents, and admins</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-10 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-gray-900">Welcome Back</h2>
              <p className="text-muted mt-2">Sign in to continue to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <FormInput
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />

              <FormPasswordInput
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <Button
                variant="primary"
                type="submit"
                className="w-full py-4 mt-2"
                loading={loading}
                disabled={loading}
              >
                Sign In
              </Button>
            </form>

            <p className="text-center mt-8 text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-bold hover:underline text-lg">
                Create one free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}